import { deepClone } from '../../../../../../helpers';
import dayjs from '../../../../../../utils/dayjs';
import { internalDateFormat } from '../constants';
import {
    IHistoryRecord,
    ILotPenInfo,
    IFixupRecord,
    moveRecordTypes,
    IHistory,
} from '../interfaces';
import {
    IFixupRecordDeleteStatusPayload,
    IFixupRecordUpdateHeadCountPayload,
    IHeadCountFixupSlice,
} from './interface';

export const getUpdatedFixupRecordsToDelete = (
    state: IHeadCountFixupSlice,
    payload: IFixupRecordDeleteStatusPayload,
): IFixupRecord[] => {
    let currentRecordsToDelete = state.fixup.recordsToDelete;
    const { recordId, recordType, penId, isDeleted } = payload;
    const initialHistoryRecords =
        state.initialHistory.moveTypeRecordPenHeadCountChanges.filter(
            record =>
                record.recordId === recordId &&
                record.recordType === recordType &&
                (penId === -1 || record.penId === penId) &&
                !record.isInitiallyModifiedRecord,
        );

    initialHistoryRecords.forEach(initialHistoryRecord => {
        const existingFixupEntry = currentRecordsToDelete.find(
            record =>
                record.recordId === initialHistoryRecord.recordId &&
                record.type === initialHistoryRecord.recordType &&
                record.penId === initialHistoryRecord.penId,
        );
        if (isDeleted && !existingFixupEntry) {
            //if isDeleted is true, we need to ensure records exist in fixupDeletes
            currentRecordsToDelete.push({
                headCount: initialHistoryRecord.hdCountChange,
                penId: initialHistoryRecord.penId,
                recordId: initialHistoryRecord.recordId,
                type: initialHistoryRecord.recordType,
                recordDate: initialHistoryRecord.recordDate,
            });
        } else if (!isDeleted && existingFixupEntry) {
            //if isDeleted is false, we need to remove from fixupDeletes
            currentRecordsToDelete = currentRecordsToDelete.filter(
                record => record !== existingFixupEntry,
            );
        }
    });

    return currentRecordsToDelete;
};

export const getUpdatedFixupRecordsToUpdate = (
    state: IHeadCountFixupSlice,
    payload: IFixupRecordUpdateHeadCountPayload,
) => {
    let currentRecordsToUpdate = state.fixup.recordsToUpdate;

    const { recordId, recordType, updatedHdCountChange, penId, recordDate } =
        payload;

    const initialRecordInHistory =
        state.initialHistory.moveTypeRecordPenHeadCountChanges.find(
            record =>
                record.recordId === recordId &&
                record.recordType === recordType &&
                record.penId === penId,
        );

    const existingFixupEntry = currentRecordsToUpdate?.find(
        record =>
            record.recordId === recordId &&
            record.type === recordType &&
            record.penId === penId,
    );

    if (initialRecordInHistory?.hdCountChange === updatedHdCountChange) {
        //user is undoing change - remove if it exists in fixup
        if (existingFixupEntry) {
            currentRecordsToUpdate = currentRecordsToUpdate?.filter(
                record => record !== existingFixupEntry,
            );
        }
    } else {
        if (existingFixupEntry) {
            existingFixupEntry.headCount = updatedHdCountChange;
        } else {
            currentRecordsToUpdate.push({
                headCount: updatedHdCountChange,
                penId,
                recordId,
                type: recordType as moveRecordTypes,
                recordDate,
            });
        }
    }

    return currentRecordsToUpdate;
};

export const getRecordRowsAndLotPensAfterFixupByDateFromInitialLoad = (
    state: IHeadCountFixupSlice,
) => {
    const recordRowsAfterFixupByDate =
        applyFixupChangesToGetRecordRowsAfterFixupByDate(state);

    const lotPensAfterFixupByDate =
        getLotPenHeadCountsByDateFromRecordRowsAfterFixup(
            state,
            recordRowsAfterFixupByDate,
        );

    return {
        recordRowsAfterFixupByDate,
        lotPensAfterFixupByDate,
    };
};

export const getHistoryFromHistoryResponse = (
    historyResponse: IHistory,
    originalRecordId?: number,
    originalRecordType?: moveRecordTypes,
) => {
    const initialHistory = {
        ...historyResponse,
        startDate: dayjs
            .utc(historyResponse.startDate)
            .format(internalDateFormat),
        //IRFTODO bug in history - start date is utc, end date is local. This is a patch.
        endDate: dayjs(historyResponse.endDate).format(internalDateFormat),
        moveTypeRecordPenHeadCountChanges:
            historyResponse.moveTypeRecordPenHeadCountChanges.map(record => ({
                ...record,
                isInitiallyModifiedRecord:
                    record.recordId === originalRecordId &&
                    record.recordType === originalRecordType,
                isDeleted: false,
                initialHeadCount: record.hdCountChange,
            })),
    };

    return initialHistory;
};

//this method take in the initial history and fixup updates/deletes
//  it applies the fixup updates/deletes to the history to get updated history
//  it sets updated history rows in state
const applyFixupChangesToGetRecordRowsAfterFixupByDate = (
    state: IHeadCountFixupSlice,
): Partial<Record<string, IHistoryRecord[]>> => {
    const { initialHistory, fixup } = state;
    const { recordsToUpdate: fixupUpdates, recordsToDelete: fixupDeletes } =
        fixup;

    const recordRows = deepClone(
        initialHistory.moveTypeRecordPenHeadCountChanges,
    );

    //apply fixup updates
    fixupUpdates.forEach(update => {
        const historyRecordRow = recordRows.find(
            record =>
                record.recordId === update.recordId &&
                record.recordType === update.type &&
                record.penId === update.penId,
        );
        if (historyRecordRow && update.headCount) {
            historyRecordRow.hdCountChange = update.headCount;
        } else {
            console.error('Fixup update not found in history', update);
        }
    });

    //apply fixup deletes
    fixupDeletes.forEach(fixupDelete => {
        const index = recordRows.findIndex(
            record =>
                record.recordId === fixupDelete.recordId &&
                record.recordType === fixupDelete.type &&
                record.penId === fixupDelete.penId,
        );
        if (index !== -1) {
            recordRows.splice(index, 1);
        } else {
            console.error('Fixup delete not found in history', fixupDelete);
        }
    });

    const recordRowsByDate = Object.groupBy(recordRows, record =>
        dayjs(record.recordDate).format(internalDateFormat),
    );

    return recordRowsByDate;
};

const getLotPenHeadCountsByDateFromRecordRowsAfterFixup = (
    state: IHeadCountFixupSlice,
    updatedRecordRowsAfterFixupByDate: Partial<
        Record<string, IHistoryRecord[]>
    >,
): Record<string, ILotPenInfo[]> => {
    const {
        startDate,
        endDate,
        lotPensForLot: initialLotPens,
    } = state.initialHistory;

    const lotPenHeadCountsByDate: Record<string, ILotPenInfo[]> = {};

    const initialDate = dayjs(startDate);
    lotPenHeadCountsByDate[initialDate.format(internalDateFormat)] =
        deepClone(initialLotPens);

    for (
        let date = initialDate.subtract(1, 'day');
        dayjs(date).isSameOrAfter(endDate);
        date = dayjs(date).subtract(1, 'day')
    ) {
        const previousDate = date.add(1, 'day').format(internalDateFormat);

        const lotPensFromPreviousDate = lotPenHeadCountsByDate[previousDate];

        const recordRowsToUndoForDate =
            updatedRecordRowsAfterFixupByDate[previousDate];

        const lotPensForDate = [...lotPensFromPreviousDate]?.map(lotPen => {
            const updatedLotPen = deepClone(lotPen);
            recordRowsToUndoForDate
                ?.filter(record => record.penId === updatedLotPen.penId)
                .forEach(record => {
                    updatedLotPen.hdCount -= record.hdCountChange;
                });

            recordRowsToUndoForDate
                ?.filter(record => record.fromPenId === lotPen.penId)
                .forEach(record => {
                    updatedLotPen.hdCount += record.hdCountChange;
                });

            return updatedLotPen;
        });

        lotPenHeadCountsByDate[date.format(internalDateFormat)] =
            lotPensForDate;
    }

    return lotPenHeadCountsByDate;
};
